import { Link } from "@tanstack/react-router";
import { ErrorState } from "@/components/shared/error-state";
import { Skeleton } from "@/components/ui/skeleton";
import type { ExpiringBucket, ExpiringCertsInsight } from "@/hooks/use-dashboard-insights";
import { formatDateOnly } from "@/lib/dates";
import type { DetailOriginSearch } from "@/lib/detail-origin";
import { cn } from "@/lib/utils";
import { HomeCard, HomeCardHead } from "./home-card";

const BUCKET_META: Record<ExpiringBucket, { label: string; badge: string }> = {
  expired: { label: "Expired", badge: "bg-danger-bg text-danger-foreground" },
  within_30: { label: "≤ 30 days", badge: "bg-warning-bg text-warning-foreground" },
  within_60: { label: "≤ 60 days", badge: "bg-info-bg text-info-foreground" },
};

/** One count pill in the summary row. */
function CountPill({ n, label, tone }: { n: number; label: string; tone: string }) {
  return (
    <div className="flex flex-1 flex-col items-center rounded-[10px] border py-2.5">
      <span className={cn("font-display text-lg font-bold leading-none", tone)}>{n}</span>
      <span className="mt-1 text-2xs font-medium text-muted-foreground">{label}</span>
    </div>
  );
}

/**
 * Certifications expired or expiring in the next 30 / 60 days, scoped by the
 * viewer's role server-side. Rows deep-link to the person's detail page.
 */
export function ExpiringCertsCard({
  title,
  insight,
  isLoading,
  isError,
  onRetry,
  personLinkSearch,
}: {
  title: string;
  insight: ExpiringCertsInsight | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  /**
   * Origin to attach to each person link, so the person detail's breadcrumb
   * returns here (e.g. Org Health) instead of the generic Person Directory.
   * Omitted on the personal dashboard, where the catalog fallback is fine.
   */
  personLinkSearch?: DetailOriginSearch;
}) {
  const counts = insight?.counts;
  const items = insight?.items ?? [];

  return (
    <HomeCard>
      <HomeCardHead title={title} />
      <div className="px-5 py-3">
        {isLoading ? (
          <div className="space-y-3">
            <div className="flex gap-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-14 flex-1 rounded-[10px]" />
              ))}
            </div>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : isError ? (
          <ErrorState
            className="py-4"
            message="Couldn't load expiring certifications"
            onRetry={onRetry}
          />
        ) : (
          <>
            <div className="flex gap-2">
              <CountPill n={counts?.expired ?? 0} label="Expired" tone="text-danger-foreground" />
              <CountPill
                n={counts?.within30 ?? 0}
                label="Next 30 days"
                tone="text-warning-foreground"
              />
              <CountPill
                n={counts?.within60 ?? 0}
                label="Next 60 days"
                tone="text-info-foreground"
              />
            </div>

            {items.length === 0 ? (
              <p className="py-4 text-center text-xs text-muted-foreground">
                Nothing expiring in the next 60 days.
              </p>
            ) : (
              <div className="mt-2 flex flex-col">
                {items.map((item) => {
                  const meta = BUCKET_META[item.bucket];
                  return (
                    <Link
                      key={item.id}
                      to="/dashboard/persons/$personId"
                      params={{ personId: item.personId }}
                      search={personLinkSearch}
                      className="group flex items-center gap-3 border-b border-dashed py-2 last:border-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-foreground group-hover:text-brand-blue">
                          {item.personName}
                        </div>
                        <div className="truncate text-2xs text-muted-foreground">
                          {item.certificationName}
                          {formatDateOnly(item.expiresAt)
                            ? ` · ${formatDateOnly(item.expiresAt)}`
                            : ""}
                        </div>
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded-full px-2 py-0.5 text-3xs font-semibold",
                          meta.badge,
                        )}
                      >
                        {meta.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </HomeCard>
  );
}
